import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { AppState } from "@/store/root.reducer";
import { SpaceData } from "@/types/spaceData";
import User from "@/models/user.model";
import { CreateGallery, GetGalleries, GetSpaces, GetUsers } from "../store/admin.actions";
import styles from "../admin-theme.module.css";

const ADMIN_ROLES = ["GALLERY", "ADMIN", "SUPERADMIN"];
const LEGACY_SPATIAL_MIGRATION_SPACE_NAME = /^Spatial migration [a-f0-9]{24}$/i;
const DEFAULT_CAMERA = {
  position: { x: 0, y: 1.4, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  cameraSpeed: 1,
  fov: 75,
};

type State = {
  galleries?: any[];
  spaces?: SpaceData[];
  users?: User[];
  loading?: boolean;
  adminError?: string | null;
};

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const userIsAdmin = (user: User): boolean => {
  const roles = Array.isArray(user.roles) ? user.roles : user.role ? [user.role] : [];
  return roles.some((role) => ADMIN_ROLES.includes(role));
};

const isSelectableGallerySpace = (space: SpaceData): boolean => (
  !["mom", "sellermom"].includes(space.type)
  && space.data?.spatialMigration?.derived !== true
  && !LEGACY_SPATIAL_MIGRATION_SPACE_NAME.test(space.name.trim())
);

export const NewGallery = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(GetGalleries());
    dispatch(GetUsers());
    dispatch(GetSpaces());
  }, [dispatch]);

  const {
    galleries = [],
    spaces = [],
    users = [],
    loading = false,
    adminError = null,
  }: State = useSelector((state: AppState) => state.admin);

  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedSpaceId, setSelectedSpaceId] = useState("");
  const [galleryName, setGalleryName] = useState("");

  const selectableSpaces = useMemo(
    () => spaces.filter(isSelectableGallerySpace),
    [spaces]
  );

  const selectedSpace = useMemo(
    () => selectableSpaces.find((space) => space._id === selectedSpaceId),
    [selectedSpaceId, selectableSpaces]
  );
  const selectedUser = useMemo(
    () => users.find((user) => user._id === selectedUserId),
    [selectedUserId, users]
  );

  const onChangeSpace = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSpaceId = event.target.value;
    setSelectedSpaceId(nextSpaceId);
    const nextSpace = selectableSpaces.find((space) => space._id === nextSpaceId);
    if (!galleryName.trim() && selectedUser && nextSpace) {
      setGalleryName(`${selectedUser.name} - ${nextSpace.name}`);
    }
  };

  const onCreate = (event: React.FormEvent) => {
    event.preventDefault();
    const name = galleryName.trim();
    if (!selectedSpace || !selectedUserId || !name) return;

    const systemTemplate = galleries.find((gallery) => (
      String(gallery?.spaceId || gallery?.space?._id || gallery?.space || "") === selectedSpace._id
      && !gallery?.user
    ));
    const templateData = systemTemplate?.data ? cloneJson(systemTemplate.data) : {};
    const templateMood = Number(templateData.mood);
    const moods = selectedSpace.data?.moods;
    const mood = Number.isInteger(templateMood)
      && templateMood >= 0
      && (!Array.isArray(moods) || templateMood < moods.length)
      ? templateMood
      : 0;

    const gallery = {
      name,
      user: selectedUserId,
      spaceId: selectedSpace._id,
      spaceType: selectedSpace.type,
      data: {
        ...templateData,
        mood,
        camera: templateData.camera || DEFAULT_CAMERA,
        title: name,
      },
      published: false,
    };

    dispatch(CreateGallery({ gallery, navigate }));
  };

  return (
    <div className="container py-3">
      <h1 className="h4 mb-3">Create Gallery</h1>
      <form className={styles.newGalleryForm} onSubmit={onCreate}>
        <div className={styles.newGalleryField}>
          <label htmlFor="new-gallery-owner" className="form-label">Gallery owner</label>
          <select
            id="new-gallery-owner"
            aria-label="Gallery owner"
            className={`form-select ${styles.lightSelect}`}
            value={selectedUserId}
            onChange={(event) => setSelectedUserId(event.target.value)}
            required
          >
            <option disabled value="">Select user</option>
            {users.filter((user) => !userIsAdmin(user)).map((user) => (
              <option key={user._id} value={user._id}>{user.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.newGalleryField}>
          <label htmlFor="new-gallery-space" className="form-label">Gallery Space</label>
          <select
            id="new-gallery-space"
            aria-label="Gallery Space"
            className={`form-select ${styles.lightSelect}`}
            value={selectedSpaceId}
            onChange={onChangeSpace}
            required
          >
            <option disabled value="">Select Space</option>
            {selectableSpaces.map((space) => (
              <option key={space._id} value={space._id}>{space.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.newGalleryField}>
          <label htmlFor="new-gallery-name" className="form-label">Gallery name</label>
          <input
            id="new-gallery-name"
            aria-label="Gallery name"
            className={`form-control ${styles.lightInput}`}
            value={galleryName}
            onChange={(event) => setGalleryName(event.target.value)}
            maxLength={160}
            required
          />
        </div>
        <div className={styles.newGalleryFooter}>
          <span className="small text-white-50">
            The selected Space provides the empty artwork placeholders.
          </span>
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading || !selectedUserId || !selectedSpaceId || !galleryName.trim()}
          >
            {loading ? "Creating..." : "Create Gallery"}
          </button>
        </div>
        {adminError ? (
          <div className="text-danger" role="alert">{adminError}</div>
        ) : null}
      </form>
    </div>
  );
};
